// VRT kit builder — instant quote + design brief wizard.
//
// Flow: choose garments & quantities → live indicative quote → describe the
// design brief (text, style chips, artwork, reference links, project
// details) → submit → confirmation.
//
// Portability: this file knows nothing about Shopify. It reads a JSON config
// island (garment catalogue, discount tiers, shipping estimate, chips,
// submit/upload targets) with graceful built-in defaults, and calls a submit
// adapter chosen by config.submit.mode and an upload adapter chosen by
// config.upload.mode. The same file runs inside a Shopify section
// (config.submit.mode === 'contact', config.upload.mode === 'shopify-line-item'
// once a merchant configures a placeholder variant) and inside the
// standalone/index.html host (config.submit.mode === 'fetch', no upload).
//
// Focus-safety: the full wizard shell is built ONCE via `this.innerHTML =`
// in connectedCallback. Every later state change — filters, quantities,
// step nav, chips, links, files — is a targeted querySelector + class/
// attribute/textContent toggle, NEVER a second full-innerHTML render, so the
// brief <textarea> and text inputs never lose focus/caret/value mid-flow.

const DEFAULT_CONFIG = {
  garments: [
    { key: 'run-ss', label: 'Running Shirt', note: 'Short sleeve', ph: 'run shirt', price: 5500, sp: 'RTX', cat: 'T', order: 10 },
    { key: 'run-ls', label: 'Running Shirt', note: 'Long sleeve', ph: 'run shirt', price: 6200, sp: 'RTX', cat: 'T', order: 11 },
    { key: 'singlet', label: 'Running Singlet', note: '', ph: 'singlet', price: 4800, sp: 'RT', cat: 'T', order: 12 },
    { key: 'tank', label: 'Tank Top', note: '', ph: 'tank', price: 4500, sp: 'RX', cat: 'T', order: 13 },
    { key: 'cyc-jersey', label: 'Cycling Jersey', note: '', ph: 'cycling jersey', price: 8900, sp: 'C', cat: 'T', order: 20 },
    { key: 'gilet', label: 'Cycling Gilet', note: 'Wind vest', ph: 'cycling gilet', price: 7900, sp: 'C', cat: 'T', order: 21 },
    { key: 'baseball', label: 'Baseball Jersey', note: '', ph: 'baseball jersey', price: 6800, sp: 'B', cat: 'T', order: 30 },
    { key: 'polo', label: 'Polo Shirt', note: '', ph: 'polo', price: 5800, sp: 'XB', cat: 'T', order: 40 },
    { key: 'hoodie', label: 'Hoodie', note: '', ph: 'hoodie', price: 8500, sp: 'RTCBXKMFGDS', cat: 'T', order: 41 },
    { key: 'bra', label: 'Sports Bra', note: '', ph: 'sports bra', price: 5200, sp: 'RTCX', cat: 'T', order: 42 },
    { key: 'shorts', label: 'Shorts', note: '', ph: 'shorts', price: 5500, sp: 'RTX', cat: 'B', order: 50 },
    { key: 'leggings', label: 'Leggings', note: '', ph: 'leggings', price: 7200, sp: 'RTX', cat: 'B', order: 51 },
    { key: 'bib', label: 'Bib Shorts', note: '', ph: 'bib shorts', price: 11000, sp: 'C', cat: 'B', order: 52 },
    { key: 'bb-pants', label: 'Baseball Pants', note: '', ph: 'baseball pants', price: 6500, sp: 'B', cat: 'B', order: 53 },
    { key: 'track', label: 'Tracksuit Bottoms', note: '', ph: 'track bottoms', price: 7800, sp: 'RTBX', cat: 'B', order: 54 },
    { key: 'sleeves', label: 'Arm Sleeves', note: '', ph: 'arm sleeves', price: 2800, sp: 'RCB', cat: 'A', order: 60 },
    { key: 'headband', label: 'Headband', note: '', ph: 'headband', price: 1800, sp: 'RT', cat: 'A', order: 61 },
    { key: 'gaiter', label: 'Neck Gaiter', note: '', ph: 'neck gaiter', price: 2200, sp: 'RTC', cat: 'A', order: 62 },
    { key: 'cap', label: 'Baseball Cap', note: '', ph: 'baseball cap', price: 3200, sp: 'BX', cat: 'A', order: 63 },
  ],
  sports: [
    { label: 'All Sports', code: null },
    { label: 'Running', code: 'R' },
    { label: 'Trail', code: 'T' },
    { label: 'Cycling', code: 'C' },
    { label: 'Baseball', code: 'B' },
    { label: 'Training', code: 'X' },
    { label: 'Basketball', code: 'K' },
    { label: 'Mountain Bike', code: 'M' },
    { label: 'Fishing', code: 'F' },
    { label: 'Golf', code: 'G' },
    { label: 'Badminton', code: 'D' },
    { label: 'Swimming', code: 'S' },
  ],
  categories: [
    { label: 'All', code: null },
    { label: 'Tops', code: 'T' },
    { label: 'Bottoms', code: 'B' },
    { label: 'Accessories', code: 'A' },
  ],
  discountTiers: [
    { min: 10, rate: 0.10 },
    { min: 25, rate: 0.18 },
    { min: 50, rate: 0.25 },
    { min: 100, rate: 0.32 },
  ],
  shipping: { base: 1500, perUnit: 160, freeOver: 50000 },
  currencySymbol: '$',
  chips: ['Bold', 'Minimal', 'Retro', 'Topographic', 'High-vis', 'Vintage', 'Monochrome', 'Gradient'],
  submit: { mode: 'none' },
  upload: { mode: 'none' },
  briefCta: 'Send for concept creation →',
  briefDisclaimer: "Our designers picks up your brief and send the 3D concepts within 24 hours. No charge until you approve. We'll get your size choices after you approve the design(s).",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
const DRAFT_STORAGE_KEY = 'vrt-kb-draft-v1';

function esc(str) {
  return String(str == null ? '' : str).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
}

let uploadSeq = 0;

class VrtKitBuilder extends HTMLElement {
  connectedCallback() {
    this.config = this.readConfig();
    this.state = {
      phase: 'quote',
      sportCode: null,
      catCode: null,
      qty: {},
      brief: '',
      files: [],
      links: [],
      crew: '',
      deadline: '',
      email: '',
      ref: null,
    };
    this.applyDraft(this.loadDraft());

    this.innerHTML = this.template();
    this.cacheEls();
    this.bindEvents();

    this.briefEl.value = this.state.brief;
    this.crewEl.value = this.state.crew;
    this.deadlineEl.value = this.state.deadline;
    this.emailEl.value = this.state.email;

    this.renderPills('sport');
    this.renderPills('cat');
    this.renderGarments();
    this.renderQuote();
    this.renderChips();
    this.renderLinks();
    this.renderFiles();
    const resumePhase = this.state.phase === 'brief' && this.computeQuote().units > 0 ? 'brief' : 'quote';
    this.showPhase(resumePhase);
  }

  // ---------------------------------------------------------------------
  // Draft persistence — a refresh/accidental-close mid-brief would otherwise
  // silently drop garments/qty, brief text, links, project details and any
  // already-uploaded file URLs. sessionStorage (not localStorage) so a
  // long-abandoned tab doesn't resurrect a stale draft days later; cleared
  // on successful submit or explicit restart.
  // ---------------------------------------------------------------------

  persist() {
    try {
      const draft = {
        phase: this.state.phase,
        qty: this.state.qty,
        brief: this.state.brief,
        links: this.state.links,
        crew: this.state.crew,
        deadline: this.state.deadline,
        email: this.state.email,
        // In-flight/failed uploads hold an AbortController and no durable
        // URL — not resumable across a reload, so only 'done' files (a real
        // CDN URL) are worth restoring.
        files: this.state.files
          .filter((f) => f.status === 'done')
          .map((f) => ({ id: f.id, name: f.name, status: f.status, url: f.url })),
      };
      sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
    } catch (e) {
      // Private-mode storage quota or disabled storage — draft recovery is
      // best-effort, never block the wizard on it.
    }
  }

  loadDraft() {
    try {
      const raw = sessionStorage.getItem(DRAFT_STORAGE_KEY);
      if (!raw) return null;
      const draft = JSON.parse(raw);
      return draft && typeof draft === 'object' ? draft : null;
    } catch (e) {
      return null;
    }
  }

  applyDraft(draft) {
    if (!draft) return;
    if (draft.qty && typeof draft.qty === 'object') this.state.qty = draft.qty;
    if (typeof draft.brief === 'string') this.state.brief = draft.brief;
    if (Array.isArray(draft.links)) this.state.links = draft.links;
    if (typeof draft.crew === 'string') this.state.crew = draft.crew;
    if (typeof draft.deadline === 'string') this.state.deadline = draft.deadline;
    if (typeof draft.email === 'string') this.state.email = draft.email;
    if (Array.isArray(draft.files)) this.state.files = draft.files;
    if (draft.phase === 'brief') this.state.phase = 'brief';
  }

  clearDraft() {
    try { sessionStorage.removeItem(DRAFT_STORAGE_KEY); } catch (e) { /* noop */ }
  }

  debounce(fn, wait) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  readConfig() {
    const el = this.querySelector('[data-kb-config]');
    let parsed = {};
    if (el) {
      try { parsed = JSON.parse(el.textContent); } catch (e) { parsed = {}; }
    }
    const garments = (parsed.garments && parsed.garments.length) ? parsed.garments : DEFAULT_CONFIG.garments;
    const cfg = {
      // Stable sort — garments without an explicit `order` keep the source
      // order (collection manual sort / metaobject list / defaults array),
      // so this is a no-op until a merchant sets order values.
      garments: [...garments].sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999)),
      sports: parsed.sports || DEFAULT_CONFIG.sports,
      categories: parsed.categories || DEFAULT_CONFIG.categories,
      discountTiers: parsed.discountTiers || DEFAULT_CONFIG.discountTiers,
      shipping: parsed.shipping || DEFAULT_CONFIG.shipping,
      currencySymbol: parsed.currencySymbol || DEFAULT_CONFIG.currencySymbol,
      chips: parsed.chips || DEFAULT_CONFIG.chips,
      submit: parsed.submit || DEFAULT_CONFIG.submit,
      upload: parsed.upload || DEFAULT_CONFIG.upload,
      briefCta: parsed.briefCta || DEFAULT_CONFIG.briefCta,
      briefDisclaimer: parsed.briefDisclaimer || DEFAULT_CONFIG.briefDisclaimer,
    };
    return cfg;
  }

  // ---------------------------------------------------------------------
  // Shell (built once)
  // ---------------------------------------------------------------------

  template() {
    return `
      <div class="vrt-kb__stepper" data-kb-stepper>
        <button type="button" class="vrt-kb__step" data-kb-step="0">
          <span class="vrt-kb__step-num">01</span>
          <span class="vrt-kb__step-label">Quote &amp; quantities</span>
        </button>
        <button type="button" class="vrt-kb__step" data-kb-step="1">
          <span class="vrt-kb__step-num">02</span>
          <span class="vrt-kb__step-label">Design brief</span>
        </button>
      </div>

      <div class="vrt-kb__layout">
        <div class="vrt-kb__main">

          <div class="vrt-kb__phase" data-kb-phase="quote">
            <div class="vrt-kb__card vrt-kb__picker">
              <div class="vrt-kb__stepnote">Step 1 — Choose garments &amp; quantities</div>
              <div class="vrt-kb__filters">
                <div class="vrt-kb__filtergroup">
                  <div class="vrt-kb__filterlabel">Sport</div>
                  <div class="vrt-kb__pills" data-kb-pills="sport"></div>
                </div>
                <div class="vrt-kb__filtergroup">
                  <div class="vrt-kb__filterlabel">Category</div>
                  <div class="vrt-kb__pills" data-kb-pills="cat"></div>
                </div>
              </div>
              <div class="vrt-kb__garments" data-kb-garments></div>
            </div>

            <div class="vrt-kb__breakdown" data-kb-breakdown hidden></div>
          </div>

          <div class="vrt-kb__phase" data-kb-phase="brief" hidden>
            <div class="vrt-kb__card">
              <div class="vrt-kb__stepnote">Step 2 — Describe the design you want</div>
              <textarea class="vrt-kb__textarea" data-kb-brief rows="5" placeholder="Tell us the vision — team / crew name, wording on the kit, colours, style references, where logos go, any must-haves. The more detail, the sharper the concept."></textarea>
              <div class="vrt-kb__chips" data-kb-chips></div>
            </div>

            <div class="vrt-kb__card">
              <div class="vrt-kb__stepnote">Upload logos, artwork &amp; references <span class="vrt-kb__optional">(optional)</span></div>
              <div class="vrt-kb__uploadslot" data-kb-uploadslot></div>
            </div>

            <div class="vrt-kb__card">
              <div class="vrt-kb__stepnote">Reference links <span class="vrt-kb__optional">(optional)</span></div>
              <div class="vrt-kb__hint">Drop in Pinterest boards, brand sites, a kit you love, your socials — anything that helps.</div>
              <div class="vrt-kb__linkrow">
                <input type="text" class="vrt-kb__input" data-kb-linkdraft placeholder="https://…">
                <button type="button" class="vrt-kb__addbtn" data-kb-addlink>Add</button>
              </div>
              <div class="vrt-kb__linklist" data-kb-linklist></div>
            </div>

            <div class="vrt-kb__card">
              <div class="vrt-kb__stepnote">Project details</div>
              <div class="vrt-kb__detailsgrid">
                <label class="vrt-kb__field">
                  <span class="vrt-kb__fieldlabel">Crew / team name</span>
                  <input type="text" class="vrt-kb__input" data-kb-crew placeholder="e.g. Night Owls Run Club">
                </label>
                <label class="vrt-kb__field">
                  <span class="vrt-kb__fieldlabel">Need it by</span>
                  <input type="text" class="vrt-kb__input" data-kb-deadline placeholder="e.g. Race day — 12 Aug">
                </label>
                <label class="vrt-kb__field vrt-kb__field--full">
                  <span class="vrt-kb__fieldlabel">Email for the concept</span>
                  <input type="email" class="vrt-kb__input" data-kb-email placeholder="you@crew.com">
                  <span class="vrt-kb__fielderror" data-kb-emailerror hidden>Enter a valid email so we can send your concept.</span>
                </label>
              </div>
            </div>

            <button type="button" class="vrt-kb__backlink" data-kb-backtoquote>← Back to quote</button>
          </div>

        </div>

        <aside class="vrt-kb__quotecard" data-kb-quotecard>
          <div class="vrt-kb__quotehead">
            <div class="vrt-kb__quoteeyebrow" data-kb-quoteeyebrow>▲ Instant quote</div>
            <div class="vrt-kb__quotetotalrow">
              <div class="vrt-kb__quotetotal" data-kb-total>$0</div>
              <div class="vrt-kb__quotemeta" data-kb-meta></div>
            </div>
          </div>
          <div class="vrt-kb__quotebody" data-kb-quotebody></div>
          <div class="vrt-kb__quotefoot">
            <button type="button" class="vrt-kb__cta" data-kb-cta disabled>Continue to design brief →</button>
            <div class="vrt-kb__quotedisclaimer" data-kb-disclaimer>Quote is indicative &amp; includes print. Final proof confirmed before payment.</div>
          </div>
        </aside>
      </div>

      <div class="vrt-kb__done" data-kb-done hidden></div>

      <div class="vrt-kb__bar" data-kb-bar>
        <div class="vrt-kb__bar-info">
          <div class="vrt-kb__bar-caption" data-kb-bar-caption>Add garments to quote</div>
          <div class="vrt-kb__bar-total" data-kb-bar-total>$0</div>
        </div>
        <div class="vrt-kb__bar-actions">
          <button type="button" class="vrt-kb__bar-back" data-kb-bar-back hidden>← Quote</button>
          <button type="button" class="vrt-kb__bar-cta" data-kb-bar-cta disabled>Continue →</button>
        </div>
      </div>

      <dialog class="vrt-kb__zoom" data-kb-zoomdialog>
        <button type="button" class="vrt-kb__zoom-close" data-kb-zoomclose aria-label="Close full-size view">×</button>
        <img class="vrt-kb__zoom-img" data-kb-zoomimg alt="">
        <div class="vrt-kb__zoom-caption">
          <span class="vrt-kb__zoom-name" data-kb-zoomname></span>
          <span class="vrt-kb__zoom-note" data-kb-zoomnote></span>
          <span class="vrt-kb__zoom-price" data-kb-zoomprice></span>
        </div>
      </dialog>
    `;
  }

  cacheEls() {
    this.stepperEl = this.querySelector('[data-kb-stepper]');
    this.layoutEl = this.querySelector('.vrt-kb__layout');
    this.quotePhaseEl = this.querySelector('[data-kb-phase="quote"]');
    this.briefPhaseEl = this.querySelector('[data-kb-phase="brief"]');
    this.breakdownEl = this.querySelector('[data-kb-breakdown]');
    this.garmentsEl = this.querySelector('[data-kb-garments]');
    this.quotecardEl = this.querySelector('[data-kb-quotecard]');
    this.quoteEyebrowEl = this.querySelector('[data-kb-quoteeyebrow]');
    this.totalEl = this.querySelector('[data-kb-total]');
    this.metaEl = this.querySelector('[data-kb-meta]');
    this.quotebodyEl = this.querySelector('[data-kb-quotebody]');
    this.ctaEl = this.querySelector('[data-kb-cta]');
    this.disclaimerEl = this.querySelector('[data-kb-disclaimer]');
    this.doneEl = this.querySelector('[data-kb-done]');
    this.barEl = this.querySelector('[data-kb-bar]');
    this.barCaptionEl = this.querySelector('[data-kb-bar-caption]');
    this.barTotalEl = this.querySelector('[data-kb-bar-total]');
    this.barBackEl = this.querySelector('[data-kb-bar-back]');
    this.barCtaEl = this.querySelector('[data-kb-bar-cta]');
    this.zoomDialogEl = this.querySelector('[data-kb-zoomdialog]');
    this.zoomImgEl = this.querySelector('[data-kb-zoomimg]');
    this.zoomNameEl = this.querySelector('[data-kb-zoomname]');
    this.zoomNoteEl = this.querySelector('[data-kb-zoomnote]');
    this.zoomPriceEl = this.querySelector('[data-kb-zoomprice]');

    this.briefEl = this.querySelector('[data-kb-brief]');
    this.chipsEl = this.querySelector('[data-kb-chips]');
    this.uploadSlotEl = this.querySelector('[data-kb-uploadslot]');
    this.linkDraftEl = this.querySelector('[data-kb-linkdraft]');
    this.linklistEl = this.querySelector('[data-kb-linklist]');
    this.crewEl = this.querySelector('[data-kb-crew]');
    this.deadlineEl = this.querySelector('[data-kb-deadline]');
    this.emailEl = this.querySelector('[data-kb-email]');
    this.emailErrorEl = this.querySelector('[data-kb-emailerror]');

    this.hiddenForm = this.config.submit && this.config.submit.formSelector
      ? document.querySelector(this.config.submit.formSelector)
      : null;
    // No submit-event guard here: the form holds only hidden inputs (no
    // focusable/submit-triggering elements), so nothing can fire a stray
    // native submit on it. Shopify's own contact-form captcha script listens
    // for a real 'submit' event at the document level to inject its token —
    // see submitBrief()'s use of Shopify.captcha.protect().
  }

  bindEvents() {
    this.addEventListener('click', this.onClick.bind(this));
    this.debouncedPersist = this.debounce(() => this.persist(), 400);

    this.briefEl.addEventListener('input', () => { this.state.brief = this.briefEl.value; this.debouncedPersist(); });
    this.crewEl.addEventListener('input', () => { this.state.crew = this.crewEl.value; this.debouncedPersist(); });
    this.deadlineEl.addEventListener('input', () => { this.state.deadline = this.deadlineEl.value; this.debouncedPersist(); });
    this.emailEl.addEventListener('input', () => {
      this.state.email = this.emailEl.value;
      if (!this.emailErrorEl.hidden) this.emailErrorEl.hidden = true;
      this.debouncedPersist();
    });
    this.linkDraftEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); this.addLinkNow(); }
    });

    // Drop the src on close so reopening a different garment never flashes
    // the previous image while the new one loads.
    this.zoomDialogEl.addEventListener('close', () => {
      this.zoomImgEl.removeAttribute('src');
    });

    // Delegated on the (stable) garments container rather than the individual
    // <input> — renderGarments() rebuilds those on every filter change, but
    // a typed-but-unblurred quantity must never trigger that full rebuild
    // itself (see setQtyFromInput) or the field would lose focus/caret mid-type.
    this.garmentsEl.addEventListener('input', (e) => {
      const t = e.target.closest('[data-kb-qtyinput]');
      if (t) this.setQtyFromInput(t);
    });
    this.garmentsEl.addEventListener('change', (e) => {
      const t = e.target.closest('[data-kb-qtyinput]');
      if (!t) return;
      const key = t.closest('[data-kb-garment]').dataset.key;
      t.value = String(this.state.qty[key] || 0);
    });
  }

  onClick(e) {
    let t;

    if ((t = e.target.closest('[data-kb-zoomview]'))) {
      this.openZoom(t.closest('[data-kb-garment]').dataset.key);
      return;
    }
    if (e.target.closest('[data-kb-zoomclose]') || e.target === this.zoomDialogEl) {
      // Direct hits on the <dialog> itself are backdrop/padding clicks —
      // clicks on its content target the inner elements instead.
      this.zoomDialogEl.close();
      return;
    }
    if ((t = e.target.closest('[data-kb-step]'))) {
      const i = Number(t.dataset.kbStep);
      if (i === 0) this.showPhase('quote');
      else if (this.computeQuote().units > 0) this.showPhase('brief');
      return;
    }
    if ((t = e.target.closest('[data-kb-pill]'))) {
      const kind = t.dataset.kind;
      const code = t.dataset.code === '' ? null : t.dataset.code;
      if (kind === 'sport') this.state.sportCode = code;
      else this.state.catCode = code;
      this.renderPills(kind);
      this.renderGarments();
      return;
    }
    if ((t = e.target.closest('[data-kb-inc]'))) {
      this.setQty(t.closest('[data-kb-garment]').dataset.key, 1);
      return;
    }
    if ((t = e.target.closest('[data-kb-dec]'))) {
      this.setQty(t.closest('[data-kb-garment]').dataset.key, -1);
      return;
    }
    if ((t = e.target.closest('[data-kb-cta]'))) {
      if (this.computeQuote().units > 0) this.showPhase('brief');
      return;
    }
    if ((t = e.target.closest('[data-kb-chip]'))) {
      const label = t.dataset.label;
      const cur = this.briefEl.value.trim();
      this.briefEl.value = (cur ? cur + ', ' : '') + label.toLowerCase();
      this.state.brief = this.briefEl.value;
      this.persist();
      return;
    }
    if ((t = e.target.closest('[data-kb-choosefiles]'))) {
      this.fileInputEl.click();
      return;
    }
    if ((t = e.target.closest('[data-kb-fileremove]'))) {
      this.removeFile(t.dataset.id);
      return;
    }
    if ((t = e.target.closest('[data-kb-addlink]'))) {
      this.addLinkNow();
      return;
    }
    if ((t = e.target.closest('[data-kb-linkremove]'))) {
      this.removeLink(Number(t.dataset.index));
      return;
    }
    if ((t = e.target.closest('[data-kb-backtoquote]')) || (t = e.target.closest('[data-kb-bar-back]'))) {
      this.showPhase('quote');
      return;
    }
    if ((t = e.target.closest('[data-kb-restart]'))) {
      this.restart();
      return;
    }
    if ((t = e.target.closest('[data-kb-bar-cta]'))) {
      if (this.state.phase === 'quote') {
        if (this.computeQuote().units > 0) this.showPhase('brief');
      } else {
        this.attemptSubmit();
      }
      return;
    }
    if ((t = e.target.closest('[data-kb-submit]'))) {
      this.attemptSubmit();
    }
  }

  // ---------------------------------------------------------------------
  // Quote math
  // ---------------------------------------------------------------------

  money(cents) {
    const dollars = Math.round(cents) / 100;
    return this.config.currencySymbol + dollars.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  discountRate(units) {
    let rate = 0;
    this.config.discountTiers.forEach((t) => { if (units >= t.min) rate = t.rate; });
    return rate;
  }

  discountLabel(units) {
    const tiers = this.config.discountTiers;
    let active = null;
    tiers.forEach((t) => { if (units >= t.min) active = t; });
    if (active) return `${active.min}+ · ${Math.round(active.rate * 100)}% off`;
    if (units > 0 && tiers.length) {
      const next = tiers[0];
      return `Add ${next.min - units} for ${Math.round(next.rate * 100)}% off`;
    }
    return 'No discount yet';
  }

  computeQuote() {
    const byKey = {};
    this.config.garments.forEach((g) => { byKey[g.key] = g; });
    const lines = Object.keys(this.state.qty)
      .filter((k) => this.state.qty[k] > 0 && byKey[k])
      .map((k) => {
        const g = byKey[k];
        const qty = this.state.qty[k];
        return { key: k, label: g.label, note: g.note, price: g.price, qty, ext: g.price * qty };
      });
    const units = lines.reduce((a, l) => a + l.qty, 0);
    const subtotal = lines.reduce((a, l) => a + l.ext, 0);
    const rate = this.discountRate(units);
    const discount = Math.round(subtotal * rate);
    const afterDiscount = subtotal - discount;
    const ship = this.config.shipping;
    const freeShip = afterDiscount >= ship.freeOver;
    const shipping = units === 0 ? 0 : (freeShip ? 0 : Math.round(ship.base + units * ship.perUnit));
    const total = afterDiscount + shipping;
    return { lines, units, subtotal, rate, discount, shipping, freeShip, total };
  }

  setQty(key, delta) {
    const q = this.state.qty;
    const n = Math.max(0, (q[key] || 0) + delta);
    if (n === 0) delete q[key]; else q[key] = n;
    this.renderGarments();
    this.renderQuote();
    this.persist();
  }

  // Typed quantity entry — updates state + the one affected card in place
  // (badge, selected state, minus-button disabled state) instead of calling
  // renderGarments(), which would rebuild every card and drop focus/caret
  // out of the field on every keystroke.
  setQtyFromInput(inputEl) {
    const key = inputEl.closest('[data-kb-garment]').dataset.key;
    const n = Math.max(0, parseInt(inputEl.value, 10) || 0);
    if (n === 0) delete this.state.qty[key]; else this.state.qty[key] = n;
    this.updateGarmentCard(key, n);
    this.renderQuote();
    this.persist();
  }

  updateGarmentCard(key, qty) {
    const card = this.garmentsEl.querySelector(`[data-kb-garment][data-key="${key}"]`);
    if (!card) return;
    card.classList.toggle('is-selected', qty > 0);
    const decBtn = card.querySelector('[data-kb-dec]');
    if (decBtn) decBtn.disabled = qty === 0;
    const media = card.querySelector('.vrt-kb__garment-media');
    let badge = media.querySelector('.vrt-kb__garment-badge');
    if (qty > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'vrt-kb__garment-badge';
        media.appendChild(badge);
      }
      badge.textContent = qty;
    } else if (badge) {
      badge.remove();
    }
  }

  // Full-size garment view — native <dialog> (top-layer, so it can't be
  // clipped by any transformed/filtered ancestor). Only reachable when the
  // garment has a real image: the zoom button isn't rendered otherwise.
  openZoom(key) {
    const g = this.config.garments.find((x) => x.key === key);
    if (!g || !g.img) return;
    this.zoomImgEl.src = g.imgFull || g.img;
    this.zoomImgEl.alt = g.label;
    this.zoomNameEl.textContent = g.label;
    this.zoomNoteEl.textContent = g.note || '';
    this.zoomNoteEl.hidden = !g.note;
    this.zoomPriceEl.textContent = `${this.money(g.price)} /unit`;
    this.zoomDialogEl.showModal();
  }

  // ---------------------------------------------------------------------
  // Renderers (targeted — no full-shell re-render)
  // ---------------------------------------------------------------------

  renderPills(kind) {
    const wrap = this.querySelector(`[data-kb-pills="${kind}"]`);
    let list = kind === 'sport' ? this.config.sports : this.config.categories;
    if (kind === 'sport') {
      list = list.filter((item) => !item.code || this.config.garments.some((g) => g.sp.includes(item.code)));
    }
    const active = kind === 'sport' ? this.state.sportCode : this.state.catCode;
    wrap.innerHTML = list.map((item) => {
      const isActive = item.code === active;
      return `<button type="button" class="vrt-kb__pill${isActive ? ' is-active' : ''}" data-kb-pill data-kind="${kind}" data-code="${esc(item.code || '')}">${esc(item.label)}</button>`;
    }).join('');
  }

  renderGarments() {
    const sc = this.state.sportCode;
    const cc = this.state.catCode;
    const filtered = this.config.garments.filter((g) => (!sc || g.sp.includes(sc)) && (!cc || g.cat === cc));
    this.garmentsEl.innerHTML = filtered.map((g) => {
      const qty = this.state.qty[g.key] || 0;
      const selected = qty > 0;
      return `
        <div class="vrt-kb__garment${selected ? ' is-selected' : ''}" data-kb-garment data-key="${esc(g.key)}">
          <div class="vrt-kb__garment-media vrt-ph">
            ${g.img
              ? `<img class="vrt-ph__img" src="${esc(g.img)}" alt="${esc(g.label)}" loading="lazy">
            <button type="button" class="vrt-kb__garment-zoombtn" data-kb-zoomview aria-label="View ${esc(g.label)} full size"></button>`
              : `<span class="vrt-ph__label">[ ${esc(g.ph)} ]</span>`}
            ${selected ? `<span class="vrt-kb__garment-badge">${qty}</span>` : ''}
          </div>
          <div class="vrt-kb__garment-name">${esc(g.label)}</div>
          ${g.note ? `<div class="vrt-kb__garment-note">${esc(g.note)}</div>` : '<div class="vrt-kb__garment-note"></div>'}
          <div class="vrt-kb__garment-price">${esc(this.money(g.price))}<span class="vrt-kb__garment-unit"> /unit</span></div>
          <div class="vrt-kb__stepperctl">
            <button type="button" class="vrt-kb__stepbtn" data-kb-dec${qty === 0 ? ' disabled' : ''}>−</button>
            <input type="text" inputmode="numeric" pattern="[0-9]*" class="vrt-kb__stepqty" data-kb-qtyinput value="${qty}" aria-label="${esc(g.label)} quantity">
            <button type="button" class="vrt-kb__stepbtn vrt-kb__stepbtn--inc" data-kb-inc>+</button>
          </div>
        </div>
      `;
    }).join('');
  }

  quoteBodyMarkup(q) {
    if (q.units === 0) {
      return '<div class="vrt-kb__quoteempty">Add garments and quantities<br>to see your instant quote.</div>';
    }
    const lines = q.lines.map((l) => `
      <div class="vrt-kb__quoteline">
        <div>
          <div class="vrt-kb__quoteline-name">${esc(l.label)}</div>
          <div class="vrt-kb__quoteline-detail">${l.qty} × ${esc(this.money(l.price))}</div>
        </div>
        <div class="vrt-kb__quoteline-total">${esc(this.money(l.ext))}</div>
      </div>
    `).join('');
    const shipNote = q.freeShip ? ` (free over ${this.money(this.config.shipping.freeOver)})` : '';
    return `
      <div>${lines}</div>
      <div class="vrt-kb__quotetotals">
        <div class="vrt-kb__quotetotalline"><span>Subtotal</span><span>${esc(this.money(q.subtotal))}</span></div>
        <div class="vrt-kb__quotetotalline vrt-kb__quotetotalline--discount"><span>Volume discount</span><span>${q.discount > 0 ? '− ' + esc(this.money(q.discount)) : '—'}</span></div>
        <div class="vrt-kb__quotetotalline"><span>Shipping${shipNote}</span><span>${q.units === 0 ? '—' : (q.freeShip ? 'FREE' : esc(this.money(q.shipping)))}</span></div>
      </div>
    `;
  }

  renderQuote() {
    const q = this.computeQuote();
    const canContinue = q.units > 0;
    const isBrief = this.state.phase === 'brief';

    this.totalEl.textContent = this.money(q.total);
    this.metaEl.innerHTML = `${q.units} ${q.units === 1 ? 'unit' : 'units'}<br>${esc(this.discountLabel(q.units))}`;
    this.quoteEyebrowEl.textContent = isBrief ? '▲ Your quote' : '▲ Instant quote';

    const bodyMarkup = this.quoteBodyMarkup(q);
    this.quotebodyEl.innerHTML = bodyMarkup;
    this.breakdownEl.innerHTML = bodyMarkup;
    this.breakdownEl.hidden = q.units === 0;

    if (isBrief) {
      this.ctaEl.textContent = this.config.briefCta;
      this.ctaEl.disabled = false;
      this.ctaEl.setAttribute('data-kb-submit', '');
      this.ctaEl.removeAttribute('data-kb-cta');
      this.disclaimerEl.textContent = this.config.briefDisclaimer;
    } else {
      this.ctaEl.textContent = 'Continue to design brief →';
      this.ctaEl.disabled = !canContinue;
      this.ctaEl.setAttribute('data-kb-cta', '');
      this.ctaEl.removeAttribute('data-kb-submit');
      this.disclaimerEl.textContent = 'Quote is indicative & includes print. Final proof confirmed before payment.';
    }

    this.barTotalEl.textContent = this.money(q.total);
    if (isBrief) {
      this.barCaptionEl.textContent = `Your quote · ${q.units} ${q.units === 1 ? 'unit' : 'units'}`;
      this.barBackEl.hidden = false;
      this.barCtaEl.textContent = 'Send brief →';
      this.barCtaEl.disabled = false;
    } else {
      this.barCaptionEl.textContent = canContinue ? `${q.units} ${q.units === 1 ? 'unit' : 'units'} · ${this.discountLabel(q.units)}` : 'Add garments to quote';
      this.barBackEl.hidden = true;
      this.barCtaEl.textContent = 'Continue →';
      this.barCtaEl.disabled = !canContinue;
    }
  }

  renderChips() {
    this.chipsEl.innerHTML = this.config.chips.map((label) => (
      `<button type="button" class="vrt-kb__chip" data-kb-chip data-label="${esc(label)}">+ ${esc(label)}</button>`
    )).join('');
  }

  renderLinks() {
    this.linklistEl.innerHTML = !this.state.links.length ? '' : this.state.links.map((url, i) => `
      <div class="vrt-kb__linkitem">
        <span class="vrt-kb__linkurl">↗ ${esc(url)}</span>
        <button type="button" class="vrt-kb__linkremove" data-kb-linkremove data-index="${i}">×</button>
      </div>
    `).join('');
    this.persist();
  }

  addLinkNow() {
    const raw = this.linkDraftEl.value.trim();
    if (!raw) return;
    const url = /^https?:\/\//i.test(raw) ? raw : ('https://' + raw);
    this.state.links.push(url);
    this.linkDraftEl.value = '';
    this.renderLinks();
  }

  removeLink(index) {
    this.state.links.splice(index, 1);
    this.renderLinks();
  }

  // ---------------------------------------------------------------------
  // Upload — native via a merchant-configured placeholder line item.
  //
  // Cart-level attributes (/cart/update.js `attributes[...]`) do NOT accept
  // file uploads (confirmed: hard 422 "Cart attributes contain invalid
  // data"). Only line-item properties on /cart/add.js do, returning a real
  // CDN /uploads/... URL. So each file is added as its own qty-1 line item
  // against a merchant-created $0 placeholder variant (config.upload.variantId)
  // with the file as a line-item property, the returned URL is captured,
  // then that exact line (by its returned `key`, never by variant id alone —
  // a real cart may already hold that variant) is immediately zeroed out via
  // /cart/change.js so it never reaches the customer's real cart/checkout.
  // ---------------------------------------------------------------------

  renderUploadSlot() {
    const cfg = this.config.upload;
    if (cfg && cfg.mode === 'shopify-line-item' && cfg.variantId) {
      this.uploadSlotEl.innerHTML = `
        <div class="vrt-kb__dropzone" data-kb-dropzone>
          <span class="vrt-kb__dropzone-label">↑ Drop or upload files</span>
          <span class="vrt-kb__dropzone-btn" data-kb-choosefiles>Choose files</span>
          <span class="vrt-kb__dropzone-hint">PNG · JPG · SVG · AI · PDF — logos, sketches, inspiration</span>
        </div>
        <div class="vrt-kb__filelist" data-kb-filelist></div>
      `;
      this.fileInputEl = document.createElement('input');
      this.fileInputEl.type = 'file';
      this.fileInputEl.multiple = true;
      this.fileInputEl.hidden = true;
      this.fileInputEl.className = 'vrt-kb__fileinput';
      this.fileInputEl.addEventListener('change', () => {
        Array.from(this.fileInputEl.files).forEach((f) => this.uploadFile(f));
        this.fileInputEl.value = '';
      });
      this.uploadSlotEl.appendChild(this.fileInputEl);

      const dropzone = this.uploadSlotEl.querySelector('[data-kb-dropzone]');
      dropzone.addEventListener('dragover', (e) => { e.preventDefault(); dropzone.classList.add('is-dragover'); });
      dropzone.addEventListener('dragleave', () => dropzone.classList.remove('is-dragover'));
      dropzone.addEventListener('drop', (e) => {
        e.preventDefault();
        dropzone.classList.remove('is-dragover');
        Array.from(e.dataTransfer.files).forEach((f) => this.uploadFile(f));
      });

      this.filelistEl = this.uploadSlotEl.querySelector('[data-kb-filelist]');
    } else {
      this.fileInputEl = null;
      this.filelistEl = null;
      this.uploadSlotEl.innerHTML = `
        <div class="vrt-kb__uploadnote">Attach logos, artwork or references by replying to your confirmation email once you submit your brief — our team will match them up before starting the concept.</div>
      `;
    }
    this.renderFiles();
  }

  renderFiles() {
    if (!this.filelistEl) return;
    this.filelistEl.innerHTML = !this.state.files.length ? '' : this.state.files.map((f) => {
      const status = f.status === 'uploading' ? ' · uploading…' : (f.status === 'error' ? ' · couldn’t upload, add a link instead' : '');
      return `
        <div class="vrt-kb__fileitem${f.status === 'error' ? ' is-error' : ''}">
          <span class="vrt-kb__filename">▪ ${esc(f.name)}${status}</span>
          <button type="button" class="vrt-kb__fileremove" data-kb-fileremove data-id="${esc(f.id)}">×</button>
        </div>
      `;
    }).join('');
    this.persist();
  }

  removeFile(id) {
    const entry = this.state.files.find((f) => f.id === id);
    if (entry && entry.abort) entry.abort.abort();
    this.state.files = this.state.files.filter((f) => f.id !== id);
    this.renderFiles();
  }

  uploadFile(file) {
    if (file.size > MAX_UPLOAD_BYTES) return;
    const id = 'kbf' + (uploadSeq += 1);
    const abort = new AbortController();
    this.state.files.push({ id, name: file.name, status: 'uploading', url: null, abort });
    this.renderFiles();

    const cfg = this.config.upload;
    const formData = new FormData();
    formData.append('id', cfg.variantId);
    formData.append('quantity', 1);
    formData.append('properties[kb-upload]', file, file.name);

    fetch(cfg.addUrl || '/cart/add.js', { method: 'POST', body: formData, signal: abort.signal })
      .then((res) => res.ok ? res.json() : Promise.reject(new Error('add failed')))
      .then((data) => {
        const url = data.properties && data.properties['kb-upload'];
        const entry = this.state.files.find((f) => f.id === id);
        if (!entry) return; // removed while in flight
        if (url) {
          entry.status = 'done';
          entry.url = url;
        } else {
          entry.status = 'error';
        }
        this.renderFiles();
        // Best-effort cleanup — the line item is a transient upload vehicle,
        // never meant to sit in the customer's real cart. If this fails the
        // URL is already captured for submission; a stray $0 line may remain
        // until the customer next views their cart (accepted risk).
        if (data.key) {
          fetch(cfg.changeUrl || '/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: data.key, quantity: 0 }),
          }).catch(() => {});
        }
      })
      .catch(() => {
        const entry = this.state.files.find((f) => f.id === id);
        if (entry) { entry.status = 'error'; this.renderFiles(); }
      });
  }

  // ---------------------------------------------------------------------
  // Phase / step navigation
  // ---------------------------------------------------------------------

  showPhase(phase) {
    this.state.phase = phase;
    this.persist();

    const inFlow = phase === 'quote' || phase === 'brief';
    this.stepperEl.hidden = !inFlow;
    this.layoutEl.hidden = !inFlow;
    this.barEl.hidden = !inFlow;
    this.doneEl.hidden = phase !== 'done';

    if (inFlow) {
      this.quotePhaseEl.hidden = phase !== 'quote';
      this.briefPhaseEl.hidden = phase !== 'brief';
      if (phase === 'brief' && !this.uploadInitialized) {
        this.uploadInitialized = true;
        this.renderUploadSlot();
      }
      this.syncStepper();
      this.renderQuote();
    }
  }

  syncStepper() {
    const canBrief = this.computeQuote().units > 0;
    this.stepperEl.querySelectorAll('[data-kb-step]').forEach((el) => {
      const i = Number(el.dataset.kbStep);
      const active = (this.state.phase === 'quote' && i === 0) || (this.state.phase === 'brief' && i === 1);
      const clickable = i === 0 || (i === 1 && canBrief);
      el.classList.toggle('is-active', active);
      el.classList.toggle('is-disabled', !clickable);
    });
  }

  // ---------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------

  attemptSubmit() {
    const email = this.emailEl.value.trim();
    if (!EMAIL_RE.test(email)) {
      this.emailErrorEl.hidden = false;
      this.emailEl.focus();
      return;
    }
    this.state.email = email;
    this.state.ref = 'K' + Math.floor(10000 + Math.random() * 90000);
    this.submitBrief();
    this.renderDone();
    this.showPhase('done');
    this.clearDraft();
  }

  submitBrief() {
    const mode = this.config.submit && this.config.submit.mode;
    const q = this.computeQuote();
    const quoteLines = q.lines.map((l) => `${l.label} × ${l.qty} = ${this.money(l.ext)}`).join('\n');
    const uploaded = this.state.files.filter((f) => f.status === 'done');
    const artwork = uploaded.length
      ? uploaded.map((f) => `${f.name} — ${f.url}`).join('\n')
      : (this.state.files.length ? 'Upload attempted but did not complete — see reply.' : 'None uploaded — may attach via reply email.');

    const payload = {
      ref: this.state.ref,
      email: this.state.email,
      crew: this.state.crew,
      deadline: this.state.deadline,
      units: q.units,
      quote: `${quoteLines}\nSubtotal: ${this.money(q.subtotal)}\nVolume discount: ${q.discount > 0 ? '-' + this.money(q.discount) : '—'}\nShipping: ${q.freeShip ? 'FREE' : this.money(q.shipping)}\nTotal: ${this.money(q.total)}`,
      links: this.state.links.join('\n'),
      artwork,
      brief: this.state.brief,
    };

    if (mode === 'contact' && this.hiddenForm) {
      this.fillContactForm(this.hiddenForm, payload);
      this.sendContactFormWithCaptcha();
    } else if (mode === 'fetch' && this.config.submit.url) {
      fetch(this.config.submit.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).catch(() => {});
    }
  }

  // Shopify's contact-form spam protection (hCaptcha here) needs its token
  // solved before the POST or the backend hard-rejects with "Missing CAPTCHA
  // token" (confirmed live). Our hidden form never gets real user
  // focusin/change events (we set .value directly), so it's never
  // auto-bound the way a normal contact form is. Shopify.captcha.protect()
  // binds+renders the (invisible) widget, but its callback fires on BIND,
  // not on SOLVE — the response field is still empty right after it fires
  // (confirmed live). Invisible hCaptcha requires an explicit
  // hcaptcha.execute(widgetId, {async:true}) call to actually run the
  // check and populate the token; that's the documented, standard hCaptcha
  // JS API for invisible mode, independent of Shopify's own wrapper.
  sendContactFormWithCaptcha() {
    let sent = false;
    const send = () => {
      if (sent) return;
      sent = true;
      const formData = new FormData(this.hiddenForm);
      fetch(this.hiddenForm.action, { method: 'POST', body: formData })
        .then((res) => {
          // A non-OK response (network-level success, storefront-level
          // failure) still needs the guaranteed fallback: a real
          // full-navigation submit.
          if (!res.ok) this.hiddenForm.submit();
        })
        .catch(() => {
          this.hiddenForm.submit();
        });
    };

    const executeAndSend = () => {
      const start = Date.now();
      const tryExecute = () => {
        const widgetEl = this.hiddenForm.querySelector('[data-hcaptcha-widget-id]');
        const widgetId = widgetEl && widgetEl.dataset.hcaptchaWidgetId;
        if (window.hcaptcha && widgetId) {
          Promise.resolve(window.hcaptcha.execute(widgetId, { async: true })).then(send).catch(send);
        } else if (Date.now() - start < 8000) {
          // The widget id can take several seconds to attach to the DOM
          // after protect()'s bind-callback fires (~5-6s observed) — poll
          // for it rather than assume it's immediate.
          setTimeout(tryExecute, 150);
        } else {
          send();
        }
      };
      tryExecute();
    };

    const captcha = window.Shopify && window.Shopify.captcha;
    if (captcha && typeof captcha.protect === 'function') {
      captcha.protect(this.hiddenForm, executeAndSend);
      // Fail-open if the challenge never resolves (blocked script, a rare
      // visible-challenge fallback taking a while, slow network) — still
      // attempt delivery rather than hang forever; the !res.ok →
      // form.submit() fallback above still covers a missing token. Set
      // comfortably above the ~6-8s observed widget-bind + solve time.
      setTimeout(send, 20000);
    } else {
      send();
    }
  }

  fillContactForm(form, payload) {
    const setField = (name, value) => {
      const input = form.querySelector(`[name="contact[${name}]"]`);
      if (input) input.value = value;
    };
    setField('Ref', payload.ref);
    setField('email', payload.email);
    setField('Crew', payload.crew);
    setField('Deadline', payload.deadline);
    setField('Units', String(payload.units));
    setField('Quote', payload.quote);
    setField('Links', payload.links || '(none provided)');
    setField('Artwork', payload.artwork);
    setField('body', payload.brief || '(no additional brief provided)');
  }

  // ---------------------------------------------------------------------
  // Done panel (built once per completion — the wizard body it replaces
  // holds no live-focused inputs at that point, so a fresh innerHTML here
  // carries none of the single-render risk the shell avoids elsewhere)
  // ---------------------------------------------------------------------

  renderDone() {
    const q = this.computeQuote();
    const summary = [
      { k: 'Garments', v: `${q.lines.length} style${q.lines.length === 1 ? '' : 's'}` },
      { k: 'Total units', v: String(q.units) },
      { k: 'Quote', v: this.money(q.total) },
      { k: 'Concept back in', v: '1–2 days' },
    ];
    const nextSteps = [
      { n: '01', t: 'Designer picks it up', d: 'We match your brief, files and links to a designer who knows your sport.' },
      { n: '02', t: 'Concept + proof', d: 'You get a kit concept and print-ready proof to review — tweak until it’s right.' },
      { n: '03', t: 'Approve & print', d: 'Sign off, pay, and we print. Delivered in 3–7 business days.' },
    ];

    this.doneEl.innerHTML = `
      <div class="vrt-kb__donecard">
        <div class="vrt-kb__donehead">
          <div class="vrt-kb__donemark">✓</div>
          <h2 class="vrt-kb__donetitle">Brief received.<br>We're on the concept.</h2>
          <p class="vrt-kb__donebody">Your quote and design brief are with our team. A designer turns it into a kit concept and print-ready proof, then sends it to <span data-kb-doneemail></span> for sign-off. Typically back within 1–2 business days.</p>
          <div class="vrt-kb__doneref">REF #VRT-${esc(this.state.ref || '—')}</div>
        </div>
        <div class="vrt-kb__donesummary">
          ${summary.map((r) => `<div class="vrt-kb__donecell"><div class="vrt-kb__donecell-k">${esc(r.k)}</div><div class="vrt-kb__donecell-v">${esc(r.v)}</div></div>`).join('')}
        </div>
        <div class="vrt-kb__donesteps">
          ${nextSteps.map((n) => `<div class="vrt-kb__donestep"><div class="vrt-kb__donestep-n">${esc(n.n)}</div><div class="vrt-kb__donestep-t">${esc(n.t)}</div><div class="vrt-kb__donestep-d">${esc(n.d)}</div></div>`).join('')}
        </div>
        <div class="vrt-kb__doneactions">
          <button type="button" class="vrt-kb__restart" data-kb-restart>← Start another kit</button>
          <a class="vrt-btn vrt-btn--outline" href="${esc((this.config.collectionUrl) || '/')}">Shop the range →</a>
        </div>
      </div>
    `;
    this.doneEl.querySelector('[data-kb-doneemail]').textContent = this.state.email || 'your inbox';
  }

  restart() {
    this.clearDraft();
    this.state = {
      phase: 'quote', sportCode: null, catCode: null, qty: {},
      brief: '', files: [], links: [], crew: '', deadline: '', email: '', ref: null,
    };
    this.briefEl.value = '';
    this.crewEl.value = '';
    this.deadlineEl.value = '';
    this.emailEl.value = '';
    this.emailErrorEl.hidden = true;
    this.linkDraftEl.value = '';
    this.renderPills('sport');
    this.renderPills('cat');
    this.renderGarments();
    this.renderLinks();
    this.renderFiles();
    this.showPhase('quote');
  }
}

if (!customElements.get('vrt-kit-builder')) {
  customElements.define('vrt-kit-builder', VrtKitBuilder);
}
